import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SocialShareButtons from "../components/SocialShareButtons";
import { ArrowLeft, Clock, ExternalLink, Newspaper } from "lucide-react";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function TransferNewsDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initial = readSsgInitialData(slug);
  const [article, setArticle] = useState(initial && initial.title ? initial : null);
  const [loading, setLoading] = useState(!(initial && initial.title));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    const fresh = readSsgInitialData(slug);
    setArticle(fresh && fresh.title ? fresh : null);
    setLoading(!(fresh && fresh.title));
    setNotFound(false);
    axios
      .get(`${API}/transfers/news/by-slug/${slug}`)
      .then((res) => {
        if (alive) {
          setArticle(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading || (!article && !notFound)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading…" path={`/transfers/news/${slug}`} />
        <div className="flex items-center justify-center py-40">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="transfer-news-notfound">
        <Navbar />
        <SEO title="Transfer news not found" path={`/transfers/news/${slug}`} noindex={true} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1
            className="text-2xl font-bold text-slate-700 mb-2"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            ARTICLE NOT FOUND
          </h1>
          <p className="text-slate-500 mb-6">
            The transfer news article you requested could not be found.
          </p>
          <Link
            to="/transfers"
            className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all transfers
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const description = (article.body || article.title || "")
    .slice(0, 160)
    .replace(/\s+/g, " ")
    .trim();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Organization", name: article.source || "All WHUFC" },
    publisher: {
      "@type": "Organization",
      name: "All WHUFC",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/og-image.png` },
    },
    mainEntityOfPage: `${BASE_URL}/transfers/news/${article.slug}`,
    image: article.image_url ? [article.image_url] : undefined,
    description,
    articleSection: "Transfers",
    about: { "@type": "SportsTeam", name: "West Ham United" },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="transfer-news-detail-page">
      <SEO
        title={article.title}
        path={`/transfers/news/${article.slug}`}
        description={description || `${article.title} — West Ham United transfer news.`}
        type="article"
        image={article.image_url}
        jsonLd={jsonLd}
        seoTitle={article.seo_title}
        seoDescription={article.seo_description}
        keywords={article.seo_keywords}
        imageAlt={article.image_alt}
      />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#7C2C3B] mb-6"
          data-testid="transfer-news-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <header className="mb-6">
          <div className="text-xs font-semibold text-[#7C2C3B] uppercase tracking-wide mb-2">
            <Link to="/transfers" className="hover:underline">
              West Ham Transfers
            </Link>{" "}
            · News
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#0F172A] leading-tight mb-3"
            style={{ fontFamily: "Barlow Condensed" }}
            data-testid="transfer-news-h1"
          >
            {article.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="font-medium text-[#7C2C3B]">{article.source || "Editorial"}</span>
            {article.published_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {formatDate(article.published_at)}
              </span>
            )}
          </div>
        </header>

        {article.image_url && (
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full rounded-lg mb-6 object-cover max-h-[420px]"
          />
        )}

        <h2
          className="text-xl font-bold text-[#0F172A] mb-3"
          style={{ fontFamily: "Barlow Condensed" }}
          data-testid="transfer-news-summary-h2"
        >
          THE STORY
        </h2>
        <div className="text-lg text-slate-700 leading-relaxed mb-8 whitespace-pre-wrap">
          {article.body}
        </div>

        {article.source_url && (
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#7C2C3B] hover:bg-[#63232F] text-white px-5 py-3 rounded-lg font-semibold transition-colors"
            data-testid="transfer-news-source-btn"
          >
            Read the full article at {article.source || "source"}
            <ExternalLink className="w-4 h-4" />
          </a>
        )}

        <div className="mt-10 pt-6 border-t border-slate-200">
          <SocialShareButtons shareText={article.title} />
        </div>

        <div className="mt-8">
          <Link
            to="/transfers"
            className="inline-flex items-center gap-2 text-sm text-[#2BAEE5] font-medium hover:underline"
            data-testid="transfer-news-back-to-list"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all West Ham transfers
          </Link>
        </div>
      </article>
      <Footer />
    </div>
  );
}
